// kontaktKonflikt.ts — zwei Leute bearbeiten denselben Kontakt (28.09.2026).
//
// Die Akte merkt sich beim Laden den Bearbeitungsstand (`version`) und die ids
// der Kindzeilen. Das Sperren schickt beides mit (`erwarteterStand`,
// `bekannteZeilen`). Hat inzwischen jemand anderes gespeichert, antwortet der
// Server mit 409 (apps/api/src/contactBearbeitungsstand.ts) — dann rechnet
// `zusammenfuehren` die eigenen Änderungen auf den neuen Stand um:
//
//   eigene Änderung   = Feld unterscheidet sich zwischen Ladestand und Maske
//   fremde Änderung   = Feld unterscheidet sich zwischen Ladestand und neuem Stand
//
//   nur eigene        → eigener Wert
//   nur fremde        → fremder Wert (bleibt erhalten)
//   beide, gleich     → kein Konflikt
//   beide, verschieden → echter Konflikt: der Anwender entscheidet je Feld
//
// Verglichen wird auf dem Formular-State, also genau dem, woraus der Diff-PUT
// entsteht. Ein Konflikt in einer Liste (z. B. `weitereKommunikation`) ist
// deshalb ein Konflikt der ganzen Liste — gröber, aber nie still falsch.

import type { FormState } from "./formUtils";

/** Antwort des Servers bei 409 `KONTAKT_GEAENDERT`. */
export interface KontaktKonfliktInfo {
  aktuellerStand: number | null;
  geaendertAm: string | null;
  geaendertVon: { id: string; name: string | null } | null;
}

export const KONFLIKT_CODE = "KONTAKT_GEAENDERT";

/** Liest die 409-Antwort; `null`, wenn es kein Bearbeitungskonflikt ist. */
export function konfliktInfoAus(body: unknown): KontaktKonfliktInfo | null {
  const b = (body ?? {}) as Record<string, unknown>;
  // Nest verpackt ein Objekt in der ConflictException manchmal unter `message`.
  const k = (b.code === KONFLIKT_CODE ? b : (b.message as Record<string, unknown>)) ?? {};
  if ((k as Record<string, unknown>).code !== KONFLIKT_CODE) return null;
  const r = k as Record<string, unknown>;
  const von = r.geaendertVon as { id?: unknown; name?: unknown } | null | undefined;
  return {
    aktuellerStand: typeof r.aktuellerStand === "number" ? r.aktuellerStand : null,
    geaendertAm: typeof r.geaendertAm === "string" ? r.geaendertAm : null,
    geaendertVon: von && von.id != null ? { id: String(von.id), name: von.name ? String(von.name) : null } : null,
  };
}

/** „Anna Alpha hat … am 28.09.2026 um 14:03 geändert" — die Teile für den Dialog. */
export function konfliktWerWann(info: KontaktKonfliktInfo | null): { wer: string; wann: string | null } {
  const wer = info?.geaendertVon?.name?.trim() || "Jemand anderes";
  const d = info?.geaendertAm ? new Date(info.geaendertAm) : null;
  const wann =
    d && !Number.isNaN(d.getTime())
      ? `am ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })} um ${d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr`
      : null;
  return { wer, wann };
}

/** Die ids der Kindzeilen, die der Client beim Laden kannte (`bekannteZeilen`). */
export function bekannteZeilenVon(kontakt: Record<string, unknown> | null | undefined): Record<string, number[]> {
  const ids = (liste: unknown): number[] =>
    (Array.isArray(liste) ? liste : [])
      .map((z) => Number((z as { id?: unknown })?.id))
      .filter((n) => Number.isInteger(n) && n > 0);
  return {
    communications: ids(kontakt?.communications),
    addresses: ids(kontakt?.addresses),
    bankAccounts: ids(kontakt?.bankAccounts),
  };
}

/** Stand aus dem geladenen Kontakt — `null` bei einem Server ohne Stand. */
export function standVon(kontakt: Record<string, unknown> | null | undefined): number | null {
  const v = kontakt?.version;
  return typeof v === "number" && Number.isInteger(v) ? v : null;
}

/**
 * Ergänzt den Diff-Rumpf um die Schutzfelder. Kindzeilen nur, wenn der Rumpf
 * eine Kindliste schreibt — sonst gibt es nichts zu schützen.
 */
export function rumpfMitStand(
  body: Record<string, unknown>,
  stand: number | null,
  bekannt: Record<string, number[]> | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...body };
  if (stand != null) out.erwarteterStand = stand;
  if (bekannt && ["communications", "addresses", "bankAccounts"].some((k) => k in body)) {
    out.bekannteZeilen = bekannt;
  }
  return out;
}

/** Leerwerte gleich behandeln; JSON schlüsselsortiert vergleichen. */
const norm = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  const t = s.trim();
  if (!/^[[{]/.test(t)) return t;
  try {
    return JSON.stringify(JSON.parse(t), (_k, x) =>
      x && typeof x === "object" && !Array.isArray(x)
        ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
        : x,
    );
  } catch {
    return t;
  }
};

export interface FeldKonflikt {
  /** Schlüssel im Formular-State (erster; weitere Schreibweisen in `felder`). */
  feld: string;
  /** Alle Schlüssel dieses Konflikts (z. B. `street` und `strasse`). */
  felder: string[];
  /** Lesbarer Name für die Liste im Dialog. */
  label: string;
  meins: string;
  deren: string;
}

export interface Zusammenfuehrung {
  /** Neuer Stand + eigene Änderungen, Konfliktfelder vorerst mit dem eigenen Wert. */
  zusammengefuehrt: FormState;
  konflikte: FeldKonflikt[];
}

/**
 * Die eigenen Änderungen (Ladestand → Maske) auf den neuen Stand anwenden.
 * Konfliktfelder tragen im Ergebnis zunächst den EIGENEN Wert —
 * `entscheidungenAnwenden` setzt, was der Anwender anders wählt.
 */
export function zusammenfuehren(args: { geladen: FormState; aktuell: FormState; neu: FormState }): Zusammenfuehrung {
  const { geladen, aktuell, neu } = args;
  const zusammengefuehrt: FormState = { ...neu };
  const konflikte: FeldKonflikt[] = [];
  const gesehen = new Map<string, FeldKonflikt>();
  for (const feld of new Set([...Object.keys(geladen), ...Object.keys(aktuell), ...Object.keys(neu)])) {
    const g = norm(geladen[feld]);
    const a = norm(aktuell[feld]);
    const n = norm(neu[feld]);
    const meinsGeaendert = a !== g;
    if (!meinsGeaendert) continue; // nur fremd oder gar nicht geändert → neuer Stand
    if (feld in aktuell) zusammengefuehrt[feld] = aktuell[feld];
    else delete zusammengefuehrt[feld];
    const derenGeaendert = n !== g;
    if (!derenGeaendert || n === a) continue;
    const label = feldLabel(feld);
    // Doppelte Schreibweisen desselben Feldes (street/strasse …) nur einmal zeigen.
    const vorhanden = gesehen.get(label);
    if (vorhanden) {
      vorhanden.felder.push(feld);
      continue;
    }
    const k: FeldKonflikt = { feld, felder: [feld], label, meins: anzeige(aktuell[feld]), deren: anzeige(neu[feld]) };
    gesehen.set(label, k);
    konflikte.push(k);
  }
  return { zusammengefuehrt, konflikte };
}

/** Wählt der Anwender bei einem Konflikt „neuer Stand", gilt der fremde Wert. */
export function entscheidungenAnwenden(
  z: Zusammenfuehrung,
  neu: FormState,
  derenBehalten: ReadonlySet<string>,
): FormState {
  const out: FormState = { ...z.zusammengefuehrt };
  for (const k of z.konflikte) {
    if (!derenBehalten.has(k.feld)) continue;
    // Nur die Schlüssel, die selbst im Konflikt stehen — eine eigene,
    // konfliktfreie Änderung unter demselben Namen bleibt.
    for (const feld of k.felder) {
      if (feld in neu) out[feld] = neu[feld];
      else delete out[feld];
    }
  }
  return out;
}

const anzeige = (v: unknown): string => {
  const s = v == null ? "" : String(v).trim();
  if (!s) return "(leer)";
  if (/^[[{]/.test(s)) {
    try {
      const j = JSON.parse(s);
      if (Array.isArray(j)) return j.length === 0 ? "(leer)" : `${j.length} Einträge`;
    } catch {
      /* Text */
    }
  }
  return s.length > 80 ? s.slice(0, 77) + " …" : s;
};

/** Lesbare Namen der häufigsten Felder; Rest: der Schlüssel selbst. */
const LABELS: Record<string, string> = {
  lastname: "Nachname",
  firstname: "Vorname",
  name: "Firmenname",
  nickname: "Rufname",
  anrede: "Anrede",
  title: "Titel",
  dateofbirth: "Geburtsdatum",
  placeofbirth: "Geburtsort",
  countryofbirth: "Geburtsland",
  birthname: "Geburtsname",
  citizenship: "Staatsangehörigkeit",
  familyStatus: "Familienstand",
  street: "Straße",
  strasse: "Straße",
  postalcode: "PLZ",
  postalCode: "PLZ",
  plz: "PLZ",
  city: "Ort",
  ort: "Ort",
  cityArea: "Ortsteil",
  ortsteil: "Ortsteil",
  country: "Land",
  land: "Land",
  additionalAddresses: "Weitere Anschriften",
  phonePrivate: "Telefon (privat)",
  mobilePrivate: "Mobil (privat)",
  emailPrivate: "E-Mail (privat)",
  phoneWork: "Telefon (geschäftlich)",
  mobileWork: "Mobil (geschäftlich)",
  emailWork: "E-Mail (geschäftlich)",
  phone: "Telefon",
  mobile: "Mobil",
  email: "E-Mail",
  website: "Website",
  weitereKommunikation: "Weitere Kommunikationswege",
  bankname: "Bank",
  iban: "IBAN",
  bic: "BIC",
  accountHolder: "Kontoinhaber",
  additionalBanks: "Weitere Bankverbindungen",
  remark: "Bemerkung",
  employer: "Arbeitgeber",
  position: "Beruf",
  occupationStatus: "Berufsstatus",
  hobbys: "Hobbys",
  vorlieben: "Vorlieben",
  memberships: "Mitgliedschaften",
  legalForm: "Rechtsform",
  branchengruppeId: "Branche",
  weitereBranchengruppen: "Weitere Branchen",
  commDbIds: "Kommunikation",
  commCompanies: "Kommunikation",
  addressDbId: "Anschrift",
  bankDbId: "Bankverbindung",
  zuwendungen: "Zuwendungen",
  todesfallBelege: "Belege zum Todesfall",
  verfuegungen: "Verfügungen",
};

export function feldLabel(feld: string): string {
  if (LABELS[feld]) return LABELS[feld];
  if (feld.startsWith("rel_")) return "Beziehungen";
  return feld;
}

/**
 * Status-Pille (`PUT contact/:id/kontaktstatus`): die Status, die die Akte
 * kennt — Hauptstatus plus Zusatz-Status. Der Server entfernt beim Abwählen
 * nur diese (Feld `bisher`); eine inzwischen von einem Kollegen vergebene
 * Rolle bleibt.
 */
export function bisherigeStatus(kontakt: unknown): string[] {
  const k = (kontakt ?? {}) as { status?: unknown; statusName?: unknown; extraStatuses?: unknown };
  const haupt =
    k.status && typeof k.status === "object"
      ? (k.status as { status?: unknown }).status
      : (k.status ?? k.statusName);
  const extra = Array.isArray(k.extraStatuses) ? k.extraStatuses : [];
  return [haupt, ...extra].map((v) => String(v ?? "").trim()).filter(Boolean);
}
